import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CategoryIcon from "@mui/icons-material/Category";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1, padding: theme.spacing(2), margin: theme.spacing(1),
  overflowY: "scroll", ...theme.scrollbarStyles,
  borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1],
}));

// Category Modal
const CategoryModal = ({ open, onClose, category }) => {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(category?.name || "");
    setProductId(category?.productId ? String(category.productId) : "");
    api.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {});
  }, [open, category]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome é obrigatório"); return; }
    setSaving(true);
    try {
      if (category) {
        await api.put(`/categories/${category.id}`, { name, productId: productId || null });
        toast.success("Categoria atualizada!");
      } else {
        await api.post("/categories", { name, productId: productId || null });
        toast.success("Categoria criada!");
      }
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
        <Typography fontWeight={700}>{category ? "Editar Categoria" : "Nova Categoria"}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus fullWidth label="Nome da categoria *"
          value={name} onChange={e => setName(e.target.value)}
          size="small" sx={{ mt: 1 }}
          onKeyDown={e => e.key === "Enter" && handleSave()}
        />
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Produto vinculado (opcional)</InputLabel>
          <Select value={productId} label="Produto vinculado (opcional)" onChange={e => setProductId(e.target.value)}>
            <MenuItem value="">Sem produto (global)</MenuItem>
            {products.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Categories Page
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setCategories(data || []);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleModalClose = (reload) => {
    setModalOpen(false);
    setSelectedCategory(null);
    if (reload) loadCategories();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/categories/${deletingCategory.id}`);
      toast.success("Categoria excluída");
      setCategories(prev => prev.filter(c => c.id !== deletingCategory.id));
    } catch (err) { toastError(err); }
    setDeletingCategory(null);
    setConfirmOpen(false);
  };

  const filtered = categories.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir categoria"
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        Tem certeza? As mensagens e scripts vinculados perderão esta categoria.
      </ConfirmationModal>

      <CategoryModal
        open={modalOpen}
        onClose={handleModalClose}
        category={selectedCategory}
      />

      <MainHeader>
        <Title>Categorias {categories.length > 0 ? `(${categories.length})` : ""}</Title>
        <MainHeaderButtonsWrapper>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <TextField
              placeholder="Buscar categorias..."
              type="search" size="small" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search color="secondary" /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { height: 40 } }}
            />
            <Tooltip title="Nova categoria">
              <Button variant="contained" color="primary"
                onClick={() => { setSelectedCategory(null); setModalOpen(true); }}
                sx={{ height: 40 }}>
                <AddCircleOutline />
              </Button>
            </Tooltip>
          </Box>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Produto</TableCell>
              <TableCell align="center">Mensagens</TableCell>
              <TableCell align="center">Scripts</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <CategoryIcon sx={{ fontSize: "2.5rem", color: "text.disabled" }} />
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma categoria cadastrada
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Crie categorias para organizar suas mensagens e scripts
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(c => (
                <TableRow key={c.id} hover>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {c.product ? (
                      <Chip label={c.product.name} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                    ) : (
                      <Typography variant="caption" color="textSecondary">Global</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={c.quickAnswersCount || 0} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={c.scriptsCount || 0} size="small" color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => { setSelectedCategory(c); setModalOpen(true); }}>
                      <Edit color="info" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setDeletingCategory(c); setConfirmOpen(true); }}>
                      <DeleteOutline color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Categories;
